import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  CheckCircle2,
  MessageSquare,
  Zap,
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    interests: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInterestChange = (interest) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', company: '', phone: '', message: '', interests: [] });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, title: 'Email', content: 'info@dmerus.com', link: 'mailto:info@dmerus.com' },
    { icon: Phone, title: 'Phone', content: '(941) 466-4-DME', link: 'tel:+19414664363' },
    { icon: MapPin, title: 'Address', content: '11161 E State Rd 70\nSte 110-556\nLakewood Ranch, FL 34202' },
    { icon: Clock, title: 'Hours', content: 'Mon - Fri: 8am - 6pm EST' },
  ];

  const faqs = [
    { question: 'Is DME R\'US HIPAA compliant?', answer: 'Yes! Full HIPAA compliance with enterprise-grade security, role-based access control, complete audit logging, and encrypted document storage.' },
    { question: 'How long does implementation take?', answer: 'Most DME providers are up and running within 2-4 weeks with dedicated onboarding support.' },
    { question: 'Can I integrate with my existing suppliers?', answer: 'DME R\'US comes pre-integrated with 10 major DME suppliers including NikoHealth, McKesson, Medline, and more.' },
    { question: 'What kind of support do you offer?', answer: 'Email, phone, and live chat support during business hours, plus comprehensive documentation and video tutorials.' },
  ];

  return (
    <div className="min-h-screen pt-48">
      {/* Hero */}
      <section className="py-20 px-6 lg:px-8 gradient-animated relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#014DB7]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-20 w-64 h-64 bg-[#84CC16]/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto text-center stagger-children">
          <div className="badge-blue mx-auto mb-6">
            <MessageSquare className="w-4 h-4" />
            <span>Get in Touch</span>
          </div>
          <h1 className="text-display text-[#0A1628] mb-6">
            Let's <span className="gradient-text-blue">Talk</span>
          </h1>
          <p className="text-subtitle max-w-2xl mx-auto">
            Have questions? Our team is here to help you find the perfect solution for your DME business.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 px-6 lg:px-8 bg-white relative">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="relative max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-2 stagger-children">
              <h2 className="text-3xl font-bold text-[#0A1628] mb-10">Contact Info</h2>
              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <div key={index} className="flex items-start gap-5 group card-hover p-4 -m-4 rounded-2xl">
                    <div className="w-14 h-14 bg-[#014DB7]/10 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#014DB7] transition-all duration-300 shadow-soft">
                      <item.icon className="w-6 h-6 text-[#014DB7] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0A1628] mb-1">{item.title}</h3>
                      {item.link ? (
                        <a href={item.link} className="text-[#64748B] hover:text-[#014DB7] transition-colors whitespace-pre-line">
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-[#64748B] whitespace-pre-line">{item.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-[#F8FAFC] rounded-3xl p-8 lg:p-12 border-2 border-[#014DB7]/10">
                {isSubmitted ? (
                  <div className="text-center py-12 stagger-children">
                    <div className="w-24 h-24 bg-[#84CC16]/20 rounded-full flex items-center justify-center mx-auto mb-8 animate-scale-in shadow-glow-lime">
                      <CheckCircle2 className="w-12 h-12 text-[#84CC16]" />
                    </div>
                    <h3 className="text-3xl font-bold text-[#0A1628] mb-4">Message Sent!</h3>
                    <p className="text-[#64748B] mb-8 max-w-md mx-auto text-lg">
                      We'll get back to you within 24 hours.
                    </p>
                    <Button onClick={() => setIsSubmitted(false)} variant="outline" className="btn-lift border-2 border-[#014DB7]/20 hover:border-[#014DB7] rounded-xl px-6 py-3">
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-[#0A1628] mb-2">Send a Message</h2>
                    <p className="text-[#64748B] mb-8">We'll respond within 24 hours.</p>

                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-[#0A1628] font-semibold">Full Name *</Label>
                          <Input id="name" name="name" value={formData.name} onChange={handleChange} required placeholder="John Doe" data-testid="contact-name-input" className="bg-white rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] h-12" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-[#0A1628] font-semibold">Email *</Label>
                          <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="john@company.com" data-testid="contact-email-input" className="bg-white rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] h-12" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="company" className="text-[#0A1628] font-semibold">Company</Label>
                          <Input id="company" name="company" value={formData.company} onChange={handleChange} placeholder="Acme Healthcare" data-testid="contact-company-input" className="bg-white rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] h-12" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-[#0A1628] font-semibold">Phone</Label>
                          <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="(555) 123-4567" data-testid="contact-phone-input" className="bg-white rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] h-12" />
                        </div>
                      </div>
                      <div className="space-y-3">
                        <Label className="text-[#0A1628] font-semibold">Features You're Interested In</Label>
                        <div className="grid grid-cols-2 gap-3">
                          {['Patient CRM', 'Lead Management', 'SEO Pages', 'Analytics', 'DME Supplier Network', 'Clearinghouse & RCM Integrations'].map((interest) => (
                            <div
                              key={interest}
                              onClick={() => handleInterestChange(interest)}
                              data-testid={`contact-interest-${interest.toLowerCase().replace(/[\s&]+/g, '-')}`}
                              className={`flex items-center gap-2.5 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                formData.interests.includes(interest)
                                  ? 'border-[#014DB7] bg-[#014DB7]/5'
                                  : 'border-gray-200 hover:border-[#014DB7]/30'
                              }`}
                            >
                              <Checkbox checked={formData.interests.includes(interest)} className="pointer-events-none" />
                              <Label className="text-sm cursor-pointer font-medium text-[#0A1628]">{interest}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-[#0A1628] font-semibold">Message *</Label>
                        <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} placeholder="How can we help you?" data-testid="contact-message-input" className="bg-white resize-none rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7]" />
                      </div>
                      <Button type="submit" disabled={isSubmitting} data-testid="contact-submit-btn" className="w-full btn-lift bg-[#014DB7] text-white hover:bg-[#003A8C] py-6 rounded-xl font-bold text-lg shadow-glow-blue">
                        {isSubmitting ? 'Sending...' : (<>Send Message <Send className="w-5 h-5 ml-2" /></>)}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6 lg:px-8 bg-[#F8FAFC] relative">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16 stagger-children">
            <div className="badge-lime mx-auto mb-6">
              <Zap className="w-4 h-4" />
              <span>FAQ</span>
            </div>
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">
              Common <span className="gradient-text-blue">Questions</span>
            </h2>
          </div>
          <div className="space-y-5 stagger-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="card-hover bg-white rounded-2xl p-7 border-2 border-[#014DB7]/10 hover:border-[#014DB7]/30">
                <h3 className="text-lg font-bold text-[#0A1628] mb-3">{faq.question}</h3>
                <p className="text-[#64748B] leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
